// AI 運算（plan/vms-web-v0.md §5.11 AIBOX-01/02/04）：每個家戶的 AI 運算提供者（AI box / NPU NVR）、容量條、指派。
//   - 把攝影機拖到提供者卡片上（或按「指派攝影機」）→ 選模型與靈敏度 → 平台先叫提供者 probe，不通就回原因、不送出
//   - 別台設備的攝影機一律由攝影機本身或通道的 parent（NVR / Station / 基地台）代理影像給提供者（kinio_channel，AIAG）：
//     提供者不拿攝影機帳密；可選取流方式 continuous（持續，佔 parent 一個觀看名額）/ event（事件快照）
//   - 每筆指派顯示狀態（套用中 / 執行中 / 降載 / 不可達）、實際 fps；可改派、解除
// 頁內分頁「本地 AI｜自帶 AI｜雲端 AI」（SPEC AI-01，VMS UX 評審 2026-10-13 P1-11；自帶 AI 是 SPEC #byo-ai-ui）：本地在前（多數組織沒訂雲端 AI），網址 `?tab=byo` / `?tab=cloud`；
// 雲端 AI（§5.10：訂閱、同意、用量、逐台排除、誰在分析）在 CloudAiSection。
// 提供者清單下方一行「N 台 Kinio Station 沒有 AI 運算能力（例：Windows 版）」，讓人知道不是漏掉（SPEC LAIH-L1-01）。
import { useMemo, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { useConfirm } from '../../components/ConfirmDialog';
import { useScope } from '../../lib/scope';
import { useDevices } from '../../lib/useDevices';
import { fullName, type VmsDevice } from '../../lib/devices';
import { aiApi, type AiAssignment, type AiProvider } from '../../lib/planeApi';
import { CloudAiSection } from './CloudAiSection';
import { ByoAiSection } from './ByoAiSection';
import { AssignmentSettings } from './AssignmentSettings';
import { aiModelName } from './modelName';

interface HomeProviders {
  homeId: string;
  homeName: string;
  api: string;
  canWrite: boolean;
  providers: AiProvider[];
  loading: boolean;
  error: string | null;
}

function errText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  if (!(e instanceof KinioError)) return String(e);
  const b = e.detail ?? {};
  return t(`ai.err_${e.code}`, {
    defaultValue: e.code,
    message: e.message,
    model: b.model ?? '',
    channels: b.channels ?? '',
    used: b.used ?? '',
    limit: b.limit ?? '',
    viewers: b.viewers ?? '',
    ai: b.ai ?? '',
  });
}

export function AiPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const all = useScope().visible;
  // AI 指派是家戶的設定；只經分享進來的家戶沒有
  const visible = useMemo(() => all.filter((h) => !h.external), [all]);
  const { tree, cameras } = useDevices();
  const qs = useQueries({
    queries: visible.map((h) => ({
      queryKey: ['ai-providers', h.home_id],
      queryFn: () => aiApi.providers(h.endpoints.api, h.home_id),
      refetchInterval: 5000,
    })),
  });
  const homes: HomeProviders[] = visible.map((h, i) => ({
    homeId: h.home_id,
    homeName: h.display_name || h.home_id,
    api: h.endpoints.api,
    canWrite: h.role === 'owner' || h.role === 'admin',
    providers: qs[i]?.data ?? [],
    loading: !!qs[i]?.isLoading,
    error: qs[i]?.error ? errText(t, qs[i]?.error) : null,
  }));
  const [assignFor, setAssignFor] = useState<{ home: HomeProviders; provider: AiProvider; camera?: string } | null>(null);
  const [settingsFor, setSettingsFor] = useState<{ home: HomeProviders; a: AiAssignment } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refresh = (homeId: string) => qc.invalidateQueries({ queryKey: ['ai-providers', homeId] });
  const camName = (ref: string, homeId: string) => {
    const c = cameras.find((x) => x.aid === ref && x.homeId === homeId);
    return c ? fullName(c, tree) : ref;
  };

  // 解除指派先確認、解除後提示列可〔復原〕（用同一個提供者、模型與參數、取流方式重新指派；第七輪 R7-P1-7，跟改派同一套）
  async function remove(home: HomeProviders, a: AiAssignment) {
    const cam = camName(a.device_ref, home.homeId);
    if (!(await ask({ danger: true, title: t('ai.unassign_confirm', { cam }), body: t('ai.unassign_body'), action: t('ai.unassign_action') }))) return;
    setBusy(a.assignment_id);
    setErr(null);
    try {
      await aiApi.remove(home.api, home.homeId, a.assignment_id);
      const provider = home.providers.find((p) => p.assignments.some((x) => x.assignment_id === a.assignment_id))?.device_ref ?? a.provider_ref ?? '';
      setMoved(null);
      setRemoved({ home, a, provider, text: t('ai.unassigned', { cam }) });
      await refresh(home.homeId);
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(null);
    }
  }
  async function undoRemove() {
    if (!removed) return;
    const { home, a, provider } = removed;
    setErr(null);
    try {
      const d = deliveryOf(a);
      await aiApi.assign(home.api, home.homeId, { provider, device_ref: a.device_ref, models: a.models, ...(d ? { delivery: d } : {}) });
      setRemoved(null);
      await refresh(home.homeId);
    } catch (e) {
      setErr(errText(t, e));
    }
  }

  // 改派前先確認（選了就生效太突然，第二輪 R2-P1-9）；改完提示列可〔復原〕（改回原來的提供者）
  const { ask, dialog: confirmDialog } = useConfirm();
  const [moved, setMoved] = useState<{ home: HomeProviders; id: string; from: string; text: string } | null>(null);
  const [removed, setRemoved] = useState<{ home: HomeProviders; a: AiAssignment; provider: string; text: string } | null>(null);
  const provName = (home: HomeProviders, ref: string) => home.providers.find((x) => x.device_ref === ref)?.name || t('ddev.ai_provider_unnamed');
  async function move(home: HomeProviders, a: AiAssignment, provider: string, undo = false) {
    const cam = camName(a.device_ref, home.homeId);
    const to = provName(home, provider);
    if (!undo && !(await ask({ title: t('ai.move_confirm', { cam, to }), body: t('ai.move_body'), action: t('ai.move_action') }))) return;
    setBusy(a.assignment_id);
    setErr(null);
    try {
      await aiApi.update(home.api, home.homeId, a.assignment_id, { expected_version: a.version, provider });
      const from = home.providers.find((p) => p.assignments.some((x) => x.assignment_id === a.assignment_id))?.device_ref ?? '';
      setMoved(undo ? null : { home, id: a.assignment_id, from, text: t('ai.moved', { cam, to }) });
      await refresh(home.homeId);
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(null);
    }
  }

  async function setDelivery(home: HomeProviders, a: AiAssignment, delivery: string) {
    setBusy(a.assignment_id);
    setErr(null);
    try {
      await aiApi.update(home.api, home.homeId, a.assignment_id, { expected_version: a.version, delivery });
      await refresh(home.homeId);
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(null);
    }
  }

  const anyProvider = homes.some((h) => h.providers.length > 0);
  // Kinio Station 沒有宣告 AI 運算（例 Windows 版沒有推論後端）就不在提供者清單：數出來告訴使用者不是漏掉
  const providerRefs = new Set(homes.flatMap((h) => h.providers.map((p) => `${h.homeId}/${p.device_ref}`)));
  const stationsNoAi = tree.filter(
    (d) => !d.parentAid && (d.aid.startsWith('KST-') || /^kinio-station/i.test(d.model)) && !providerRefs.has(`${d.homeId}/${d.aid}`),
  );
  const stationsWithoutAi = stationsNoAi.length;
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') === 'cloud' ? 'cloud' : sp.get('tab') === 'byo' ? 'byo' : 'local';
  const tabs = (
    <div className="dtabs" role="tablist" aria-label={t('nav.ai')}>
      {(['local', 'byo', 'cloud'] as const).map((x) => (
        <button key={x} role="tab" aria-selected={tab === x} onClick={() => setSp(x === 'local' ? {} : { tab: x }, { replace: true })}>
          {t(`ai.tab_${x}`)}
        </button>
      ))}
    </div>
  );
  if (tab === 'byo')
    return (
      <div className="page">
        <h1 className="page-title">{t('nav.ai')}</h1>
        {tabs}
        <ByoAiSection />
      </div>
    );
  if (tab === 'cloud')
    return (
      <div className="page">
        <h1 className="page-title">{t('nav.ai')}</h1>
        {tabs}
        <CloudAiSection />
      </div>
    );
  return (
    <div className="page">
      <h1 className="page-title">{t('nav.ai')}</h1>
      {tabs}
      <p className="muted small">{t('ai.help')}</p>
      {err && <p className="error">{err}</p>}
      {!anyProvider && !homes.some((h) => h.loading) && <p className="notice">{t('ai.no_provider')}</p>}
      {moved && (
        <p className="notice small" data-testid="ai-moved">
          {moved.text}{' '}
          <button
            className="btn btn-sm"
            onClick={() => {
              // 用最新的清單（版本號已經變了）
              const home = homes.find((h) => h.homeId === moved.home.homeId) ?? moved.home;
              const a = home.providers.flatMap((p) => p.assignments).find((x) => x.assignment_id === moved.id);
              if (a && moved.from) void move(home, a, moved.from, true);
            }}
          >
            {t('ai.move_undo')}
          </button>
        </p>
      )}
      {removed && (
        <p className="notice small" data-testid="ai-removed">
          {removed.text}{' '}
          <button className="btn btn-sm" onClick={() => void undoRemove()}>
            {t('ai.move_undo')}
          </button>
        </p>
      )}
      {homes
        .filter((h) => h.providers.length > 0 || h.error)
        .map((home) => {
          const assigned = new Set(home.providers.flatMap((p) => p.assignments.map((a) => a.device_ref)));
          const free = cameras.filter((c) => c.homeId === home.homeId && !assigned.has(c.aid));
          return (
            <section key={home.homeId} className="ai-home">
              <h2>{home.homeName}</h2>
              {home.error && <p className="error">{home.error}</p>}
              <div className="ai-grid">
                {home.providers.map((p) => {
                  const pct = Math.min(100, Math.round((p.used / Math.max(1, p.capacity.channels)) * 100));
                  return (
                    <div
                      key={p.device_ref}
                      className="card ai-provider"
                      data-provider={p.device_ref}
                      onDragOver={(e) => home.canWrite && e.preventDefault()}
                      onDrop={(e) => {
                        const ref = e.dataTransfer.getData('text/x-camera');
                        if (ref && home.canWrite) setAssignFor({ home, provider: p, camera: ref });
                      }}
                    >
                      <div className="ai-provider-head">
                        <span className={`dot ${p.online ? 'online' : 'offline'}`} />
                        <strong>{p.name || p.device_ref}</strong>
                        <span className="spacer" />
                        {home.canWrite && (
                          <button
                            className="btn btn-sm"
                            disabled={!p.online || p.used >= p.capacity.channels}
                            onClick={() => setAssignFor({ home, provider: p })}
                          >
                            {t('ai.assign')}
                          </button>
                        )}
                      </div>
                      <div className="ai-cap" title={t('ai.capacity_title', { used: p.used, total: p.capacity.channels })}>
                        <div className={`ai-cap-bar ${pct >= 90 ? 'full' : ''}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="small">
                        {t('ai.capacity', { used: p.used, total: p.capacity.channels })} · {t('ai.load', { n: p.load })} ·{' '}
                        {t('ai.fps', { n: p.capacity.fps_per_channel })}
                      </div>
                      {p.assignments.length === 0 ? (
                        <p className="muted small">{t('ai.none')}</p>
                      ) : (
                        <ul className="ai-list">
                          {p.assignments.map((a) => (
                            <li key={a.assignment_id} data-assignment={a.assignment_id}>
                              <span className="ai-cam">{camName(a.device_ref, home.homeId)}</span>
                              <span className={`badge ai-st-${a.status ?? 'pending'}`}>{t(`ai.st_${a.status ?? 'pending'}`)}</span>
                              {a.fps ? <span className="muted small">{t('ai.fps', { n: a.fps })}</span> : null}
                              <span className="muted small">
                                {a.models.map((m) => aiModelName(t, m.model)).join(t('common.list_sep'))}
                              </span>
                              {a.detail && <span className="muted small">{t(`ai.detail_${a.detail}`, { defaultValue: t('common.state_unknown') })}</span>}
                              {deliveryOf(a) && !home.canWrite && <span className="badge">{t(`ai.delivery_${deliveryOf(a)}`)}</span>}
                              {home.canWrite && (
                                <span className="ai-actions">
                                  {deliveryOf(a) && (
                                    <select
                                      className="input"
                                      value={deliveryOf(a)!}
                                      disabled={busy === a.assignment_id}
                                      aria-label={t('ai.delivery')}
                                      title={t('ai.delivery_help')}
                                      onChange={(e) => void setDelivery(home, a, e.target.value)}
                                    >
                                      <option value="continuous">{t('ai.delivery_continuous')}</option>
                                      <option value="event">{t('ai.delivery_event')}</option>
                                    </select>
                                  )}
                                  {home.providers.length > 1 && (
                                    <select
                                      className="input"
                                      value=""
                                      disabled={busy === a.assignment_id}
                                      aria-label={t('ai.move')}
                                      onChange={(e) => e.target.value && void move(home, a, e.target.value)}
                                    >
                                      <option value="">{t('ai.move')}</option>
                                      {home.providers
                                        .filter((x) => x.device_ref !== p.device_ref)
                                        .map((x) => (
                                          <option key={x.device_ref} value={x.device_ref}>
                                            {x.name || x.device_ref}
                                          </option>
                                        ))}
                                    </select>
                                  )}
                                  <button className="btn btn-sm" onClick={() => setSettingsFor({ home, a })}>
                                    {t('aiset.open')}
                                  </button>
                                  <button className="btn btn-sm" disabled={busy === a.assignment_id} onClick={() => void remove(home, a)}>
                                    {t('ai.unassign')}
                                  </button>
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
              {home.canWrite && free.length > 0 && (
                <div className="ai-free">
                  <span className="muted small">{t('ai.drag_help')}</span>
                  <div className="ai-chips">
                    {free.map((c) => (
                      <span key={c.key} className="chip" draggable onDragStart={(e) => e.dataTransfer.setData('text/x-camera', c.aid)}>
                        {fullName(c, tree)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      {/* 「這幾台 Station 沒有 AI 能力」是補充說明，放清單下方（第二輪 R2-P1-9） */}
      {stationsWithoutAi > 0 && (
        <p className="muted small" data-testid="ai-stations-without">
          {/* 直接寫是哪幾台（第七輪 B7-12：「例：Windows 版」看不出是哪一台） */}
          {stationsWithoutAi <= 3
            ? t('ai.stations_without_named', { names: stationsNoAi.map((d) => fullName(d, tree)).join(t('common.list_sep')) })
            : t('ai.stations_without', { count: stationsWithoutAi })}
        </p>
      )}
      {confirmDialog}
      {settingsFor && (
        <AssignmentSettings
          api={settingsFor.home.api}
          homeId={settingsFor.home.homeId}
          assignment={settingsFor.a}
          cam={cameras.find((c) => c.aid === settingsFor.a.device_ref && c.homeId === settingsFor.home.homeId)}
          camName={camName(settingsFor.a.device_ref, settingsFor.home.homeId)}
          close={() => setSettingsFor(null)}
          done={() => {
            void refresh(settingsFor.home.homeId);
            setSettingsFor(null);
          }}
        />
      )}
      {assignFor && (
        <AssignDialog
          home={assignFor.home}
          provider={assignFor.provider}
          initialCamera={assignFor.camera}
          cameras={cameras.filter((c) => c.homeId === assignFor.home.homeId)}
          tree={tree}
          taken={new Set(assignFor.home.providers.flatMap((p) => p.assignments.map((a) => a.device_ref)))}
          close={() => setAssignFor(null)}
          done={() => {
            void refresh(assignFor.home.homeId);
            setAssignFor(null);
          }}
        />
      )}
    </div>
  );
}

/** 經 parent 代理的指派（kinio_channel）的取流方式；提供者自己的通道回 null。 */
export function deliveryOf(a: Pick<AiAssignment, 'source'>): 'continuous' | 'event' | null {
  if (a.source?.kind !== 'kinio_channel') return null;
  return a.source.delivery === 'event' ? 'event' : 'continuous';
}

function AssignDialog(props: {
  home: HomeProviders;
  provider: AiProvider;
  initialCamera?: string;
  cameras: VmsDevice[];
  tree: VmsDevice[];
  taken: Set<string>;
  close(): void;
  done(): void;
}) {
  const { t } = useTranslation();
  const { home, provider } = props;
  const options = useMemo(() => props.cameras.filter((c) => !props.taken.has(c.aid)), [props.cameras, props.taken]);
  const [camera, setCamera] = useState(props.initialCamera ?? options[0]?.aid ?? '');
  const [models, setModels] = useState<Set<string>>(new Set(provider.capacity.models.slice(0, 1)));
  const [sensitivity, setSensitivity] = useState(60);
  const [delivery, setDelivery] = useState<'' | 'continuous' | 'event'>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cam = options.find((c) => c.aid === camera);
  // 不是提供者自己的通道 → 影像由攝影機或它的 parent 代理（可選取流方式；不填 = 平台依 parent 宣告決定）
  const proxied = !!cam && cam.parentAid !== provider.device_ref;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await aiApi.assign(home.api, home.homeId, {
        provider: provider.device_ref,
        device_ref: camera,
        models: [...models].map((m) => ({ model: m, params: { sensitivity } })),
        ...(proxied && delivery ? { delivery } : {}),
      });
      props.done();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open title={t('ai.assign_title', { name: provider.name || provider.device_ref })} onClose={props.close}>
      <div className="form">
        <label className="field">
          <span>{t('ai.camera')}</span>
          <select className="input" value={camera} onChange={(e) => setCamera(e.target.value)} aria-label={t('ai.camera')}>
            {options.map((c) => (
              <option key={c.key} value={c.aid}>
                {fullName(c, props.tree)}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="field">
          <legend>{t('ai.models')}</legend>
          {provider.capacity.models.map((m) => (
            <label key={m} className="check">
              <input
                type="checkbox"
                checked={models.has(m)}
                onChange={() =>
                  setModels((s) => {
                    const n = new Set(s);
                    if (n.has(m)) n.delete(m);
                    else n.add(m);
                    return n;
                  })
                }
              />
              {aiModelName(t, m)}
            </label>
          ))}
        </fieldset>
        <label className="field">
          <span>{t('ai.sensitivity', { n: sensitivity })}</span>
          <input
            type="range"
            min={10}
            max={100}
            step={10}
            value={sensitivity}
            onChange={(e) => setSensitivity(Number(e.target.value))}
            aria-label={t('ai.sensitivity', { n: sensitivity })}
          />
        </label>
        {proxied && (
          <label className="field">
            <span>{t('ai.delivery')}</span>
            <select className="input" value={delivery} onChange={(e) => setDelivery(e.target.value as typeof delivery)} aria-label={t('ai.delivery')}>
              <option value="">{t('ai.delivery_auto')}</option>
              <option value="continuous">{t('ai.delivery_continuous')}</option>
              <option value="event">{t('ai.delivery_event')}</option>
            </select>
            <span className="muted small">{t('ai.delivery_help')}</span>
          </label>
        )}
        {err && <p className="error">{err}</p>}
        <div className="toolbar">
          <button className="btn btn-primary" disabled={busy || !camera || models.size === 0} onClick={() => void submit()}>
            {busy ? t('ai.probing') : t('ai.assign_submit')}
          </button>
          <button className="btn" onClick={props.close}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
